// src/components/lists/BookingsList/BookingsFilterDrawer.tsx
import React, { useMemo, useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as FilterListIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { BookingsFilterState } from '../../../hooks/useBookingsFilter';
import { BookingStatus } from './bookingsListTypes';
import DateRangePicker from '../../forms/shared/DateRangePicker';

interface DrawerFilterState {
  status?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isProspect?: boolean;
  isExpired?: boolean;
  isConverted?: boolean;
}

interface BookingsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filterState: BookingsFilterState;
  onApply: (drawerFilters: DrawerFilterState) => void;
  onClear: () => void;
  isQuotations?: boolean;
}

const AccordionSectionTitle = styled(Typography)(() => ({
  fontWeight: 700,
  fontSize: '1.6rem',
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  '& .MuiAccordionSummary-content': { margin: '4px 0' },
  '& .MuiAccordionSummary-content.Mui-expanded': { margin: '4px 0' },
}));

const StyledAccordionDetails = styled(AccordionDetails)(() => ({
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  '& .MuiTypography-root': {
    fontSize: '1.6rem',
    fontWeight: 700,
    lineHeight: 1.1,
    margin: 0,
    fontFamily: theme.typography.fontFamily,
  },
}));

const BOOKING_STATUSES: BookingStatus[] = [
  'Pending',
  'Confirmed',
  'Checked In',
  'Checked Out',
  'Cancelled',
];

// Bookings: operational ordering first (check-in/out), then by client, then status, then audit.
const BOOKING_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'checkin', label: 'Check-in date' },
  { value: 'checkout', label: 'Check-out date' },
  { value: 'surname', label: 'Client surname' },
  { value: 'firstname', label: 'Client first name' },
  { value: 'status', label: 'Status' },
  { value: 'created', label: 'Created date' },
];

// Quotes: pipeline ordering first (most recently created), then dates, then by client.
// Status omitted - every row on /quotes is Status=Quotation by route definition.
const QUOTE_SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'created', label: 'Created date' },
  { value: 'checkin', label: 'Check-in date' },
  { value: 'checkout', label: 'Check-out date' },
  { value: 'surname', label: 'Client surname' },
  { value: 'firstname', label: 'Client first name' },
];

// Map the tri-state isProspect (undefined | false | true) to/from radio values.
const prospectToRadio = (v: boolean | undefined): string =>
  v === true ? 'prospect' : v === false ? 'existing' : 'all';

const radioToProspect = (v: string): boolean | undefined =>
  v === 'prospect' ? true : v === 'existing' ? false : undefined;

// Map the tri-state isConverted (undefined | true | false) to/from radio values.
const convertedToRadio = (v: boolean | undefined): string =>
  v === true ? 'converted' : v === false ? 'not_converted' : 'all';

const radioToConverted = (v: string): boolean | undefined =>
  v === 'converted' ? true : v === 'not_converted' ? false : undefined;

export const BookingsFilterDrawer: React.FC<BookingsFilterDrawerProps> = ({
  open,
  onClose,
  filterState,
  onApply,
  onClear,
  isQuotations = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sortOptions = useMemo(
    () => (isQuotations ? QUOTE_SORT_OPTIONS : BOOKING_SORT_OPTIONS),
    [isQuotations]
  );

  const [localState, setLocalState] = useState<DrawerFilterState>({});

  // Re-initialize local state from committed filter state each time drawer opens.
  useEffect(() => {
    if (open) {
      setLocalState({
        status: filterState.status,
        startDate: filterState.startDate,
        endDate: filterState.endDate,
        sortBy: filterState.sortBy,
        sortOrder: filterState.sortOrder ?? 'desc',
        isProspect: filterState.isProspect,
        isExpired: filterState.isExpired,
        isConverted: filterState.isConverted,
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLocal = (key: keyof DrawerFilterState, value: any) => {
    setLocalState((prev: DrawerFilterState) => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    onApply(localState);
    onClose();
  };

  const handleClear = () => {
    setLocalState({ sortOrder: 'desc' });
    onClear();
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '80%' : 400,
          backgroundColor: theme.palette.background.default,
          borderLeft: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: theme.palette.grey[50] }} />
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize="2rem"
              sx={{ color: theme.palette.grey[50] }}
            >
              Filters & Sort
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: theme.palette.grey[50] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
          <Accordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <AccordionSectionTitle>Sort By</AccordionSectionTitle>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <RadioGroup
                value={localState.sortBy || ''}
                onChange={(e) => setLocal('sortBy', e.target.value)}
              >
                {sortOptions.map((opt) => (
                  <StyledFormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>

              {localState.sortBy && (
                <RadioGroup
                  value={localState.sortOrder ?? 'desc'}
                  onChange={(e) =>
                    setLocal('sortOrder', e.target.value as 'asc' | 'desc')
                  }
                  row
                >
                  <StyledFormControlLabel
                    value="asc"
                    control={<Radio />}
                    label="Ascending"
                  />
                  <StyledFormControlLabel
                    value="desc"
                    control={<Radio />}
                    label="Descending"
                  />
                </RadioGroup>
              )}
            </StyledAccordionDetails>
          </Accordion>

          {!isQuotations && (
            <Accordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <AccordionSectionTitle>Booking Status</AccordionSectionTitle>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <RadioGroup
                  value={localState.status || 'all'}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLocal('status', v === 'all' ? undefined : v);
                  }}
                >
                  <StyledFormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All statuses"
                  />
                  {BOOKING_STATUSES.map((status) => (
                    <StyledFormControlLabel
                      key={status}
                      value={status}
                      control={<Radio />}
                      label={status}
                    />
                  ))}
                </RadioGroup>
              </StyledAccordionDetails>
            </Accordion>
          )}

          {isQuotations && (
            <Accordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <AccordionSectionTitle>Client Type</AccordionSectionTitle>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <RadioGroup
                  value={prospectToRadio(localState.isProspect)}
                  onChange={(e) =>
                    setLocal('isProspect', radioToProspect(e.target.value))
                  }
                >
                  <StyledFormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All clients"
                  />
                  <StyledFormControlLabel
                    value="existing"
                    control={<Radio />}
                    label="Existing only"
                  />
                  <StyledFormControlLabel
                    value="prospect"
                    control={<Radio />}
                    label="Prospects only"
                  />
                </RadioGroup>
              </StyledAccordionDetails>
            </Accordion>
          )}

          {isQuotations && (
            <Accordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <AccordionSectionTitle>Quote Status</AccordionSectionTitle>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <RadioGroup
                  value={prospectToRadio(localState.isExpired)}
                  onChange={(e) =>
                    setLocal('isExpired', radioToProspect(e.target.value))
                  }
                >
                  <StyledFormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All quotes"
                  />
                  <StyledFormControlLabel
                    value="existing"
                    control={<Radio />}
                    label="Not expired"
                  />
                  <StyledFormControlLabel
                    value="prospect"
                    control={<Radio />}
                    label="Expired"
                  />
                </RadioGroup>
              </StyledAccordionDetails>
            </Accordion>
          )}

          {isQuotations && (
            <Accordion defaultExpanded>
              <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <AccordionSectionTitle>Converted</AccordionSectionTitle>
              </StyledAccordionSummary>
              <StyledAccordionDetails>
                <RadioGroup
                  value={convertedToRadio(localState.isConverted)}
                  onChange={(e) =>
                    setLocal('isConverted', radioToConverted(e.target.value))
                  }
                >
                  <StyledFormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All quotes"
                  />
                  <StyledFormControlLabel
                    value="not_converted"
                    control={<Radio />}
                    label="Not converted"
                  />
                  <StyledFormControlLabel
                    value="converted"
                    control={<Radio />}
                    label="Converted only"
                  />
                </RadioGroup>
              </StyledAccordionDetails>
            </Accordion>
          )}

          <Accordion defaultExpanded>
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <AccordionSectionTitle>Date Range</AccordionSectionTitle>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              {/* desktopType='modal' avoids z-index conflicts with the Drawer */}
              <DateRangePicker
                mode="range"
                desktopType="modal"
                checkInDate={localState.startDate || ''}
                checkOutDate={localState.endDate || ''}
                onCheckInChange={(date) =>
                  setLocal('startDate', date || undefined)
                }
                onCheckOutChange={(date) =>
                  setLocal('endDate', date || undefined)
                }
              />
            </StyledAccordionDetails>
          </Accordion>
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 2,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClear}
            sx={{
              flex: 1,
              borderRadius: '2px',
              fontSize: '1.6rem',
              lineHeight: '1.8rem',
            }}
          >
            Reset
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApply}
            sx={{
              flex: 1,
              borderRadius: '2px',
              fontSize: '1.6rem',
              lineHeight: '1.8rem',
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BookingsFilterDrawer;
